import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { checkIn, checkOut, getEmployeeStats } from '../services/api';
import { showSuccess, showError, showWarning } from '../services/swal';

const EmployeePortal = () => {
    const [user, setUser] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        sales: { today: 0, month: 0 },
        attendance: { rate: 100, status: 'not-active' },
        tasks: { pending_count: 0, list: [] }
    });

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user'));
        setUser(userData);
        fetchStats();

        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchStats = async () => {
        try {
            const res = await getEmployeeStats();
            setStats(res.data);
        } catch (err) {
            console.error('Error fetching employee stats:', err);
        }
    };

    const handleAttendance = async (type) => {
        setLoading(true);
        if (!navigator.geolocation) {
            showError('Geolocation Error', 'Geolocation is not supported by your browser');
            setLoading(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(async (position) => {
            const coords = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            };

            try {
                if (type === 'in') {
                    await checkIn(coords);
                    showSuccess('Checked In Successfully!', 'Shift started. Have a great day!');
                } else {
                    await checkOut(coords);
                    showSuccess('Checked Out Successfully!', 'Shift ended. See you tomorrow!');
                }
                fetchStats();
            } catch (err) {
                showError('Attendance Error', err.response?.data?.message || err.message);
            } finally {
                setLoading(false);
            }
        }, (error) => {
            showWarning('Location Required', 'Unable to retrieve location. Please enable GPS for verification.');
            setLoading(false);
        });
    };

    return (
        <Layout title="Employee Command Center">
            <div className="employee-portal animate__animated animate__fadeIn">
                {/* Premium Hero Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-lg overflow-hidden position-relative card-hero" 
                             style={{ 
                                 borderRadius: '30px', 
                                 background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                                 minHeight: '240px'
                             }}>
                            {/* Decorative background icons */}
                            <div className="position-absolute top-0 end-0 p-4 opacity-10 d-none d-lg-block" style={{ fontSize: '12rem', transform: 'rotate(15deg) translate(20%, -20%)' }}>
                                <i className="bi bi-clock-fill"></i>
                            </div>
                            
                            <div className="card-body p-4 p-lg-5 d-flex flex-column justify-content-between h-100 text-white position-relative z-1 card-body-hero">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                                    <div>
                                        <h5 className="text-white text-opacity-75 fw-bold text-uppercase mb-2" style={{ letterSpacing: '1px' }}>
                                            {currentTime.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                                        </h5>
                                        <h1 className="display-3 fw-black mb-0 animate__animated animate__pulse animate__infinite animate__slow" style={{ fontWeight: 900 }}>
                                            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </h1>
                                    </div>
                                    <div className="bg-dark bg-opacity-10 backdrop-blur rounded-4 px-3 py-2 border border-white border-opacity-25 shadow-sm align-self-start align-self-md-auto">
                                        <span className={`badge rounded-circle p-2 me-2 ${stats.attendance.status === 'checked-in' ? 'bg-success animate__animated animate__flash animate__infinite' : 'bg-white bg-opacity-50'}`} style={{ width: '12px', height: '12px', display: 'inline-block' }}></span>
                                        <span className="small fw-bold text-uppercase text-white">
                                            {stats.attendance.status === 'checked-in' ? 'On Duty' : stats.attendance.status === 'checked-out' ? 'Off Duty' : 'Shift Not Started'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="mt-4 pt-4">
                                    <h2 className="fw-bold mb-1">Welcome back, {user?.name?.split(' ')[0]}! 👋</h2>
                                    <p className="text-white text-opacity-75 mb-0">Your command center is ready. Let's make today productive.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Performance Intelligence & Actions */}
                <div className="row g-4">
                    {/* Attendance Hub */}
                    <div className="col-12 col-xl-4">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '24px' }}>
                            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                                <h5 className="fw-bold text-dark mb-0"><i className="bi bi-geo-alt-fill text-primary me-2"></i>Shift Control</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="bg-light rounded-4 p-3 mb-4 border border-dashed border-primary border-opacity-25">
                                    <div className="d-flex align-items-center small text-muted lh-sm">
                                        <i className="bi bi-shield-check-fill text-success fs-4 me-3"></i>
                                        <div>
                                            <div className="fw-bold text-dark mb-1">Location Verification Active</div>
                                            Check-in requires authorized shop proximity (50m radius).
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="d-grid gap-3">
                                    <button 
                                        className={`btn btn-lg py-4 rounded-4 shadow-sm border-2 fw-bold transition-all ${stats.attendance.status === 'checked-in' ? 'btn-light text-muted opacity-50' : 'btn-primary'}`}
                                        onClick={() => handleAttendance('in')}
                                        disabled={loading || stats.attendance.status === 'checked-in'}
                                    >
                                        <i className="bi bi-box-arrow-in-right fs-4 d-block mb-1"></i>
                                        START SHIFT
                                    </button>
                                    <button 
                                        className={`btn btn-lg py-4 rounded-4 shadow-sm border-2 fw-bold transition-all ${stats.attendance.status !== 'checked-in' ? 'btn-light text-muted opacity-50' : 'btn-danger'}`}
                                        onClick={() => handleAttendance('out')}
                                        disabled={loading || stats.attendance.status !== 'checked-in'}
                                    >
                                        <i className="bi bi-box-arrow-left fs-4 d-block mb-1"></i>
                                        END SHIFT
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Performance Dashboard */}
                    <div className="col-12 col-xl-8">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '24px' }}>
                            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                                <h5 className="fw-bold text-dark mb-0"><i className="bi bi-graph-up-arrow text-primary me-2"></i>Live Performance</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-4">
                                    <div className="col-12 col-md-6 col-lg-4">
                                        <div className="p-4 rounded-4 bg-primary bg-opacity-10 border border-primary border-opacity-10 h-100">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="bg-primary text-white rounded-3 p-2 shadow-sm">
                                                    <i className="bi bi-currency-dollar fs-4"></i>
                                                </div>
                                                <span className="badge bg-primary rounded-pill px-3">Today</span>
                                            </div>
                                            <h6 className="text-muted fw-bold small text-uppercase mb-1">Sales Revenue</h6>
                                            <h3 className="fw-bold text-dark mb-0">Rs. {stats.sales.today.toLocaleString()}</h3>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-4">
                                        <div className="p-4 rounded-4 bg-success bg-opacity-10 border border-success border-opacity-10 h-100">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="bg-success text-white rounded-3 p-2 shadow-sm">
                                                    <i className="bi bi-wallet2 fs-4"></i>
                                                </div>
                                                <span className="badge bg-success rounded-pill px-3">Monthly</span>
                                            </div>
                                            <h6 className="text-muted fw-bold small text-uppercase mb-1">Total Impact</h6>
                                            <h3 className="fw-bold text-dark mb-0">Rs. {stats.sales.month.toLocaleString()}</h3>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-4">
                                        <div className="p-4 rounded-4 bg-warning bg-opacity-10 border border-warning border-opacity-10 h-100">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="bg-warning text-dark rounded-3 p-2 shadow-sm">
                                                    <i className="bi bi-calendar-check-fill fs-4"></i>
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold text-warning" style={{ fontSize: '1.2rem' }}>{stats.attendance.rate}%</div>
                                                    <small className="text-muted">Fidelity</small>
                                                </div>
                                            </div>
                                            <h6 className="text-muted fw-bold small text-uppercase mb-1">Attendance Score</h6>
                                            <div className="progress mt-2" style={{ height: '8px', borderRadius: '10px' }}>
                                                <div className="progress-bar bg-warning" style={{ width: `${stats.attendance.rate}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-2">
                                    <h6 className="fw-bold text-dark mb-3"><i className="bi bi-list-task me-2 text-primary"></i>My Assigned Operations</h6>
                                    <div className="list-group list-group-flush gap-2">
                                        {stats.tasks.list.length > 0 ? (
                                            stats.tasks.list.map(task => (
                                                <div key={task.id} className="list-group-item border rounded-4 p-3 d-flex align-items-center justify-content-between transition-all hover-light">
                                                    <div className="d-flex align-items-center">
                                                        <div className={`rounded-circle p-2 me-3 ${task.status === 'completed' ? 'bg-success text-white' : 'bg-light text-muted border'}`}>
                                                            <i className={`bi bi-${task.status === 'completed' ? 'check-lg' : 'clock'}`}></i>
                                                        </div>
                                                        <div>
                                                            <div className={`fw-bold ${task.status === 'completed' ? 'text-decoration-line-through text-muted' : 'text-dark'}`}>{task.title}</div>
                                                            <div className="small text-muted">Assigned by: {task.assigner?.name}</div>
                                                        </div>
                                                    </div>
                                                    <span className={`badge rounded-pill px-3 py-2 ${task.status === 'completed' ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                                                        {task.status.toUpperCase()}
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-4 bg-light rounded-4 border border-dashed">
                                                <i className="bi bi-stars fs-2 text-primary opacity-50 mb-2 d-block"></i>
                                                <p className="small text-muted mb-0">No pending operations. You're all caught up!</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .fw-black { font-weight: 900; }
                .backdrop-blur { backdrop-filter: blur(8px); }
                .hover-light:hover { background-color: rgba(0,0,0,0.02); transform: translateX(5px); }
                .transition-all { transition: all 0.3s ease; }
                
                /* Mobile Hero Adjustments */
                @media (max-width: 768px) {
                    .employee-portal .display-3 { font-size: 2.5rem !important; }
                    .employee-portal .card-hero { min-height: 180px !important; }
                    .employee-portal .card-body-hero { padding: 1.5rem !important; }
                }

                /* Dark Mode Support */
                [data-bs-theme="dark"] .card {
                    background-color: var(--card-bg) !important;
                    border-color: var(--border-color) !important;
                }
                [data-bs-theme="dark"] .bg-light {
                    background-color: rgba(255,255,255,0.05) !important;
                }
                [data-bs-theme="dark"] .list-group-item {
                    background-color: transparent !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .text-dark {
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .hover-light:hover {
                    background-color: rgba(255,255,255,0.05) !important;
                }
            `}</style>
        </Layout>
    );
};

export default EmployeePortal;
